import { useState, useMemo } from 'react';
import { FileText, TrendingUp, TrendingDown, Minus, ExternalLink } from 'lucide-react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell,
} from 'recharts';
import Card, { CardHeader, CardBody } from '../ui/Card';

interface Report {
  reportId: string;
  type: string;
  period: string;
  filingDate: string;
  url?: string;
  status?: string;
  metrics?: {
    revenue?: string | number | null;
    grossProfit?: string | number | null;
    operatingIncome?: string | number | null;
    netIncome?: string | number | null;
    totalAssets?: string | number | null;
    totalLiabilities?: string | number | null;
    shareholdersEquity?: string | number | null;
  } | null;
}

interface Props {
  reports: Report[];
}

// ─── Utilities ──────────────────────────────────────────────────────────────────

/** Parse any currency string/number to a value in billions. */
const parseToBillions = (value?: string | number | null): number | null => {
  if (value == null) return null;
  if (typeof value === 'number') return value / 1_000_000_000;
  const clean = value.replace(/[$,]/g, '').trim();
  if (/[Tt](?:rillion)?/i.test(clean))
    return parseFloat(clean.replace(/[A-Za-z\s]/g, '')) * 1_000;
  if (/[Bb](?:illion)?/i.test(clean))
    return parseFloat(clean.replace(/[A-Za-z\s]/g, ''));
  if (/[Mm](?:illion)?/i.test(clean))
    return parseFloat(clean.replace(/[A-Za-z\s]/g, '')) / 1_000;
  const num = parseFloat(clean);
  if (isNaN(num)) return null;
  if (Math.abs(num) >= 1_000_000) return num / 1_000_000_000;
  if (Math.abs(num) >= 1_000) return num / 1_000;
  return num;
};

const fmtB = (v: number | null): string => {
  if (v == null) return '—';
  const abs = Math.abs(v);
  if (abs >= 1_000) return `$${(v / 1_000).toFixed(1)}T`;
  if (abs >= 1) return `$${v.toFixed(1)}B`;
  if (abs >= 0.001) return `$${(v * 1_000).toFixed(0)}M`;
  return `$${(v * 1_000_000).toFixed(0)}K`;
};

const fmtDate = (d?: string | null): string => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return d; }
};

type FilterType = 'all' | 'annual' | 'quarterly' | 'with-data';

// ─── Dedup helper ───────────────────────────────────────────────────────────────
/** When multiple reports share the same period, keep the one with better data. */
function deduplicateReports(reports: Report[]): Report[] {
  const map = new Map<string, Report>();
  for (const r of reports) {
    const key = r.period;
    const existing = map.get(key);
    if (!existing) {
      map.set(key, r);
    } else {
      // Prefer the one with metrics.revenue, then the one with a filingDate
      const existingHasRev = !!(existing.metrics?.revenue);
      const newHasRev = !!(r.metrics?.revenue);
      if (!existingHasRev && newHasRev) {
        map.set(key, r);
      } else if (existingHasRev === newHasRev) {
        // Both have or both lack revenue — prefer the one with a filing date
        if (!existing.filingDate && r.filingDate) map.set(key, r);
      }
    }
  }
  return Array.from(map.values());
}

// ─── Component ──────────────────────────────────────────────────────────────────

export default function FinancialTimeline({ reports }: Props) {
  const [filter, setFilter] = useState<FilterType>('annual');

  if (reports.length === 0) {
    return (
      <Card>
        <CardHeader>
          <h3 className="text-sm font-semibold text-neutral-900">Financial Timeline</h3>
        </CardHeader>
        <CardBody>
          <div className="text-center py-8 text-neutral-500">
            <FileText className="h-8 w-8 mx-auto mb-2 text-neutral-300" />
            <p className="text-sm">No reports available</p>
          </div>
        </CardBody>
      </Card>
    );
  }

  // Deduplicate first, then sort newest-first
  const dedupedReports = deduplicateReports(reports);
  const sorted = [...dedupedReports].sort((a, b) => {
    // Sort by fiscal year desc, then by quarter desc
    const yearA = parseInt(a.period.match(/\d{4}/)?.[0] || '0');
    const yearB = parseInt(b.period.match(/\d{4}/)?.[0] || '0');
    if (yearB !== yearA) return yearB - yearA;
    // Within same year: FY > Q4 > Q3 > Q2 > Q1
    const qOrder = (p: string) => {
      if (/FY|annual/i.test(p)) return 5;
      const qMatch = p.match(/Q(\d)/);
      return qMatch ? parseInt(qMatch[1]) : 0;
    };
    return qOrder(b.period) - qOrder(a.period);
  });

  // Apply filter
  const filtered = sorted.filter(r => {
    if (filter === 'annual') return r.type === '10-K' || r.type === 'Annual' || /FY|annual/i.test(r.period);
    if (filter === 'quarterly') return r.type === '10-Q' || r.type === 'Quarterly' || /Q[1-4]/.test(r.period);
    if (filter === 'with-data') return !!(r.metrics?.revenue);
    return true;
  });

  const totalWithData = sorted.filter(r => r.metrics?.revenue).length;
  const totalWithout = sorted.length - totalWithData;

  // ─── Chart data (only reports with revenue, oldest→newest for chart) ───
  const chartData = useMemo(() => {
    const withData = filtered
      .filter(r => r.metrics?.revenue)
      .map(r => {
        const rev = parseToBillions(r.metrics?.revenue);
        const ni = parseToBillions(r.metrics?.netIncome);
        const gp = parseToBillions(r.metrics?.grossProfit);
        const isAnnual = r.type === '10-K' || r.type === 'Annual' || /FY|annual/i.test(r.period);
        return {
          period: r.period,
          revenue: rev,
          netIncome: ni,
          grossProfit: gp,
          isAnnual,
        };
      })
      .reverse(); // oldest → newest for chart
    return withData;
  }, [filtered]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-neutral-900">Financial Timeline</h3>
            <p className="text-xs text-neutral-400 mt-0.5">
              {sorted.length} reports &middot; {totalWithData} with data
            </p>
          </div>
          {/* Filter toggle */}
          <div className="inline-flex rounded-lg border border-neutral-200 bg-neutral-50 p-0.5">
            {([
              { id: 'with-data' as const, label: 'With Data' },
              { id: 'annual' as const, label: 'Annual' },
              { id: 'quarterly' as const, label: 'Quarterly' },
              { id: 'all' as const, label: 'All' },
            ]).map(({ id, label }) => (
              <button
                key={id}
                onClick={() => setFilter(id)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  filter === id
                    ? 'bg-white text-primary-700 shadow-sm'
                    : 'text-neutral-500 hover:text-neutral-800'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>

      {/* ─── Chart ─────────────────────────────────────────────────────── */}
      {chartData.length >= 2 && (
        <div className="px-4 pt-2 pb-4 border-b border-neutral-100">
          <ResponsiveContainer width="100%" height={240}>
            <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis
                dataKey="period"
                tick={{ fontSize: 10, fill: '#a3a3a3' }}
                axisLine={{ stroke: '#e5e5e5' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 10, fill: '#a3a3a3' }}
                tickFormatter={(v: number) => `$${v.toFixed(0)}B`}
                axisLine={false}
                tickLine={false}
                width={55}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e5e5e5',
                  borderRadius: '8px',
                  fontSize: '12px',
                  boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                }}
                formatter={(value: number, name: string) => [
                  `$${value?.toFixed(1)}B`,
                  name === 'revenue' ? 'Revenue' : name === 'netIncome' ? 'Net Income' : 'Gross Profit',
                ]}
                labelStyle={{ fontWeight: 600, marginBottom: 4 }}
              />
              <Legend
                wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
                formatter={(value: string) =>
                  value === 'revenue' ? 'Revenue' : value === 'netIncome' ? 'Net Income' : 'Gross Profit'
                }
              />
              <Bar dataKey="revenue" name="revenue" fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={40}>
                {chartData.map((entry, i) => (
                  <Cell
                    key={i}
                    fill={entry.isAnnual ? '#3b82f6' : '#93c5fd'}
                    fillOpacity={entry.isAnnual ? 0.85 : 0.55}
                  />
                ))}
              </Bar>
              <Line
                type="monotone"
                dataKey="netIncome"
                name="netIncome"
                stroke="#10b981"
                strokeWidth={2}
                dot={{ r: 3, fill: '#10b981', strokeWidth: 0 }}
                activeDot={{ r: 5 }}
              />
              <Line
                type="monotone"
                dataKey="grossProfit"
                name="grossProfit"
                stroke="#8b5cf6"
                strokeWidth={1.5}
                strokeDasharray="4 3"
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      <CardBody className="p-0">
        {filtered.length === 0 ? (
          <div className="text-center py-8 text-neutral-400 text-sm">
            No reports match this filter
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-neutral-200 bg-neutral-50/80">
                  <th className="text-left py-2.5 px-4 text-xs font-bold text-neutral-500 uppercase tracking-wider">Period</th>
                  <th className="text-left py-2.5 px-3 text-xs font-bold text-neutral-500 uppercase tracking-wider">Type</th>
                  <th className="text-left py-2.5 px-3 text-xs font-bold text-neutral-500 uppercase tracking-wider">Filed</th>
                  <th className="text-right py-2.5 px-3 text-xs font-bold text-neutral-500 uppercase tracking-wider">Revenue</th>
                  <th className="text-right py-2.5 px-3 text-xs font-bold text-neutral-500 uppercase tracking-wider">Net Income</th>
                  <th className="text-right py-2.5 px-3 text-xs font-bold text-neutral-500 uppercase tracking-wider">Total Assets</th>
                  <th className="text-center py-2.5 px-3 text-xs font-bold text-neutral-500 uppercase tracking-wider">Rev YoY</th>
                  <th className="text-center py-2.5 px-2 text-xs font-bold text-neutral-500 uppercase tracking-wider w-8"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((report, idx) => {
                  const rev = parseToBillions(report.metrics?.revenue);
                  const ni = parseToBillions(report.metrics?.netIncome);
                  const ta = parseToBillions(report.metrics?.totalAssets);

                  // Find previous same-type report for YoY
                  const prevSameType = filtered.slice(idx + 1).find(r =>
                    (r.type === report.type || r.period.replace(/\d{4}/, '') === report.period.replace(/\d{4}/, '')) &&
                    r.metrics?.revenue
                  );
                  const prevRev = prevSameType ? parseToBillions(prevSameType.metrics?.revenue) : null;
                  const yoyChange = (rev != null && prevRev != null && prevRev !== 0)
                    ? ((rev - prevRev) / Math.abs(prevRev)) * 100
                    : null;

                  const isAnnual = report.type === '10-K' || report.type === 'Annual' || /FY|annual/i.test(report.period);
                  const hasData = !!(report.metrics?.revenue);

                  return (
                    <tr
                      key={report.reportId || `${report.period}-${idx}`}
                      className={`border-b border-neutral-100 transition-colors ${
                        isAnnual ? 'bg-primary-50/20 hover:bg-primary-50/40' : 'hover:bg-neutral-50'
                      } ${!hasData ? 'opacity-50' : ''}`}
                    >
                      {/* Period */}
                      <td className="py-2 px-4 font-semibold text-neutral-900 whitespace-nowrap">
                        {report.period}
                      </td>

                      {/* Type badge */}
                      <td className="py-2 px-3">
                        <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                          isAnnual
                            ? 'bg-primary-100 text-primary-700'
                            : 'bg-neutral-100 text-neutral-600'
                        }`}>
                          {report.type || '—'}
                        </span>
                      </td>

                      {/* Filing date */}
                      <td className="py-2 px-3 text-neutral-500 text-xs whitespace-nowrap">
                        {fmtDate(report.filingDate)}
                      </td>

                      {/* Revenue */}
                      <td className="py-2 px-3 text-right font-semibold text-neutral-900 tabular-nums whitespace-nowrap">
                        {fmtB(rev)}
                      </td>

                      {/* Net Income */}
                      <td className={`py-2 px-3 text-right font-medium tabular-nums whitespace-nowrap ${
                        ni != null && ni < 0 ? 'text-red-600' : 'text-neutral-700'
                      }`}>
                        {fmtB(ni)}
                      </td>

                      {/* Total Assets */}
                      <td className="py-2 px-3 text-right font-medium text-neutral-600 tabular-nums whitespace-nowrap">
                        {fmtB(ta)}
                      </td>

                      {/* YoY Change */}
                      <td className="py-2 px-3 text-center whitespace-nowrap">
                        {yoyChange != null ? (
                          <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${
                            yoyChange > 0.5 ? 'text-green-600' : yoyChange < -0.5 ? 'text-red-600' : 'text-neutral-400'
                          }`}>
                            {yoyChange > 0.5 ? <TrendingUp className="w-3 h-3" /> :
                             yoyChange < -0.5 ? <TrendingDown className="w-3 h-3" /> :
                             <Minus className="w-3 h-3" />}
                            {Math.abs(yoyChange).toFixed(1)}%
                          </span>
                        ) : (
                          <span className="text-neutral-300 text-xs">—</span>
                        )}
                      </td>

                      {/* Link */}
                      <td className="py-2 px-2 text-center">
                        {report.url && (
                          <button
                            onClick={() => window.open(report.url, '_blank')}
                            className="text-neutral-400 hover:text-primary-600 transition-colors"
                            title="View SEC filing"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer summary */}
        {totalWithout > 0 && filter !== 'all' && (
          <div className="px-4 py-2 border-t border-neutral-100 text-[10px] text-neutral-400">
            {totalWithout} report{totalWithout > 1 ? 's' : ''} without financial data hidden &middot;{' '}
            <button onClick={() => setFilter('all')} className="text-primary-500 hover:text-primary-700 font-medium">
              Show all
            </button>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
